import React, { useContext, useState } from "react"
import { CALENDAR_MONTHS } from "../../utils/exports/months"
import { NavLink } from "react-router-dom"
import { db, funEU } from "../../utils/firebase"
import { AlertsContext } from "../../utils/providers/alerts"
import { formatPointsValue } from "../../utils/stableford"
import firebase from "firebase"
import moment from "moment"
import "./stableford-points.scss"

// UI components
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Tile from "../../components/structure/tile/tile"
import Navigation from "../../components/ui/navigation/navigation"
import Select from "../../components/ui/select/select"
import Button from "../../components/ui/button/button"
import Input from "../../components/ui/inputs/input"
import Badge from "../../components/ui/badge/badge"

const voidRedemption = funEU.httpsCallable("voidPartnerRedemption")

// Get the years obj
let yearsObj = {}
let nextYear = Number(moment().startOf("year").add(2, "years").format("YYYY"))
for (let i = nextYear; i >= 2018; i--) {
	yearsObj = {
		[i]: i,
		...yearsObj,
	}
}

const formatCurrency = (value) => {
	return new Intl.NumberFormat("en-GB", {
		style: "currency",
		currency: "GBP",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(value)
}

/**
 * Turn the month/year filters into a Firestore timestamp range. Returns null
 * when there is nothing to search on.
 */
const buildDateRange = (month, year) => {
	if (!year) return null

	const reference = month ? moment(`${month}-${year}`, "MMMM-YYYY") : moment(`${year}`, "YYYY")
	const unit = month ? "month" : "year"

	return {
		start: firebase.firestore.Timestamp.fromMillis(reference.clone().startOf(unit).valueOf()),
		end: firebase.firestore.Timestamp.fromMillis(reference.clone().endOf(unit).valueOf()),
	}
}

// Reports on the Stableford points spent, either against our own bookings or by
// a third-party redemption partner
export default function StablefordPoints() {
	const [activeTab, setActiveTab] = useState("BOOKINGS")
	const [loading, setLoading] = useState(false)

	const [month, setMonth] = useState("")
	const [year, setYear] = useState("")

	const [bookings, setBookings] = useState([])
	const [redemptions, setRedemptions] = useState([])

	const [voidingID, setVoidingID] = useState("")
	const [voidReason, setVoidReason] = useState("")
	const [voiding, setVoiding] = useState(false)

	const { pushAlert } = useContext(AlertsContext)

	// Fetch the relevant stableford payments on bookings
	const fetchStablefordBookings = async (range) => {
		// Find all the real stableford points payments
		const bookingDocs = await db
			.collectionGroup("payments")
			.where("payee", "==", "SYSTEM")
			.where("reference", "==", "Stableford Discount")
			.where("paid_date", ">=", range.start)
			.where("paid_date", "<=", range.end)
			.get()

		setBookings(bookingDocs.docs.map((doc) => ({ ...doc.data(), id: doc.id, bookingID: doc.ref.parent.parent.id })))
	}

	// Fetch the points redeemed by third-party partners over the same window
	const fetchPartnerRedemptions = async (range) => {
		const redemptionDocs = await db
			.collection("points_redemptions")
			.where("created", ">=", range.start)
			.where("created", "<=", range.end)
			.orderBy("created", "desc")
			.get()

		setRedemptions(redemptionDocs.docs.map((doc) => ({ ...doc.data(), id: doc.id })))
	}

	// Run the fetch for whichever report is on screen
	const fetchStats = async () => {
		const range = buildDateRange(month, year)
		if (!range) return

		setLoading(true)
		setBookings([])
		setRedemptions([])
		cancelVoid()

		try {
			if (activeTab === "BOOKINGS") {
				await fetchStablefordBookings(range)
			} else {
				await fetchPartnerRedemptions(range)
			}
		} catch (err) {
			pushAlert({
				type: "ERROR",
				title: "Fetch failed",
				body: err?.message || "Unable to load the Stableford figures.",
			})
		} finally {
			setLoading(false)
		}
	}

	// Switching report clears whatever was on screen, so the two never mix
	const switchTab = (tabID) => {
		setActiveTab(tabID)
		setBookings([])
		setRedemptions([])
		cancelVoid()
	}

	const cancelVoid = () => {
		setVoidingID("")
		setVoidReason("")
	}

	// Reverse a partner redemption, handing the points back to the client
	const confirmVoid = async (redemptionID) => {
		setVoiding(true)

		try {
			const { data } = await voidRedemption({ redemptionID, reason: voidReason.trim() })

			// Flag it locally rather than refetching the whole window
			setRedemptions((current) => current.map((entry) => (entry.id === redemptionID ? { ...entry, voided: true, void_reason: voidReason.trim() } : entry)))

			pushAlert({
				type: "SUCCESS",
				title: "Redemption voided",
				body: `${data.points} points have been returned to the client's balance.`,
			})

			cancelVoid()
		} catch (error) {
			pushAlert({
				type: "ERROR",
				title: "Void failed",
				body: error?.message || "Unable to void that redemption.",
			})
		} finally {
			setVoiding(false)
		}
	}

	// Returns each row for the bookings table
	const BookingRow = ({ details }) => {
		// Pull out the details from the details prop
		const { bookingID, paid_date, amount } = details

		// Format the "paid_date"
		const formattedDate = moment(paid_date?.seconds, "X").format("DD/MM/YYYY")

		return (
			<tr>
				<td>{formattedDate}</td>
				<td>{formatCurrency(amount)}</td>
				<td className="is-button align-right">
					<NavLink to={`booking/${bookingID}`}>View booking</NavLink>
					<a
						target="_blank"
						rel="noreferrer"
						href={`booking/${bookingID}`}>
						View in new tab
					</a>
				</td>
			</tr>
		)
	}

	// Build the total for the stableford points
	const stablefordTotal = bookings.reduce((acc, booking) => acc + booking.amount, 0)

	// Voided redemptions are excluded from the totals - the points went back
	const liveRedemptions = redemptions.filter((redemption) => redemption.voided !== true)
	const redeemedPointsTotal = liveRedemptions.reduce((acc, redemption) => acc + Number(redemption.points || 0), 0)

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Stableford Points</h1>
			</Title>

			<Navigation
				links={[
					{
						id: "BOOKINGS",
						label: "Booking redemptions",
					},
					{
						id: "PARTNERS",
						label: "Partner redemptions",
					},
				]}
				active={activeTab}
				onClick={switchTab}
			/>

			<div className="page-filters extra-stats stableford-points">
				<Select
					label="Month:"
					placeholder="Month:"
					value={month}
					activeOnHover={true}
					onSelect={(option) => setMonth(option.option)}
					options={CALENDAR_MONTHS}
				/>

				<Select
					label="Year:"
					placeholder="Year:"
					value={year}
					activeOnHover={true}
					onSelect={(option) => setYear(option.option)}
					options={yearsObj}
				/>

				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<Button
						filters={true}
						disabled={!year}
						label="Fetch stats"
						loadingText="Fetching stats..."
						loading={loading}
						onClick={() => fetchStats()}
					/>
				</div>
			</div>

			{activeTab === "BOOKINGS" && (
				<Table
					className="travellers-table"
					headings={["Assigned Date", "Stableford Amount", ""]}
					noResults={bookings.length === 0}
					noResultsMessage={"No results matching those filters"}>
					{bookings.map((booking) => (
						<BookingRow
							key={booking?.id}
							details={booking}
						/>
					))}

					<tr className="totals-row">
						<td>Totals:</td>
						<td>{formatCurrency(stablefordTotal)}</td>
						<td></td>
					</tr>
				</Table>
			)}

			{activeTab === "PARTNERS" && (
				<Table
					className="travellers-table"
					headings={["Date", "Partner", "Client", "Their reference", "Points", "Value", ""]}
					noResults={redemptions.length === 0}
					noResultsMessage={"No partner redemptions matching those filters"}>
					{redemptions.map((redemption) => (
						<tr key={redemption.id}>
							<td>{moment(redemption.created?.seconds, "X").format("DD/MM/YYYY HH:mm")}</td>
							<td>{redemption.partner_name}</td>
							<td>
								<NavLink to={`/client/${redemption.client}`}>{redemption.client_name || redemption.client_email || redemption.client}</NavLink>
							</td>
							<td>
								{redemption.reference}
								{redemption.note && <small className="stableford-points__note">{redemption.note}</small>}
							</td>
							<td>{Number(redemption.points || 0).toLocaleString("en-GB")}</td>
							<td>{formatPointsValue(redemption.points)}</td>
							<td className="align-right">
								{redemption.voided ? (
									<Badge
										type="NEGATIVE"
										label="Voided"
									/>
								) : voidingID === redemption.id ? (
									<div className="stableford-points__void">
										<Input
											placeholder="Reason (optional)"
											value={voidReason}
											onChange={setVoidReason}
										/>
										<Button
											xsSmall={true}
											label="Confirm void"
											loading={voiding}
											loadingText="Voiding..."
											onClick={() => confirmVoid(redemption.id)}
										/>
										<Button
											xsSmall={true}
											type="secondary"
											label="Cancel"
											onClick={() => cancelVoid()}
										/>
									</div>
								) : (
									<Button
										xsSmall={true}
										label="Void"
										onClick={() => {
											setVoidingID(redemption.id)
											setVoidReason("")
										}}
									/>
								)}
							</td>
						</tr>
					))}

					<tr className="totals-row">
						<td>Totals:</td>
						<td></td>
						<td></td>
						<td></td>
						<td>{redeemedPointsTotal.toLocaleString("en-GB")}</td>
						<td>{formatPointsValue(redeemedPointsTotal)}</td>
						<td></td>
					</tr>
				</Table>
			)}
		</Tile>
	)
}
